import { DashedLinesGap } from "features/CenterContent/RoleContent/ProductionDeviation/components/DashedLinesGap";
import { BoxProps, VStack } from "shared/components/Spor";
import { VariantColor } from "shared/utils/colorUtils";
import { FCWC } from "shared/utils/FCWC";
import { hideScrollbar } from "shared/utils/hideScrollbar";
import styled, { RuleSet } from "styled-components";

const SegmentedPanelContainer = styled(VStack)<{
  $panelStyle: RuleSet<object>;
  $noOverflow?: boolean;
}>`
  /* Use up all space and have no gap */
  &&& {
    flex: 1;
    align-items: stretch;
    gap: 0px;
    height: 100%; /* Is overwritten by 'flex: 1' if inside a <Flex/> */
    ${({ $noOverflow }) => !$noOverflow && `overflow-y: scroll;`}
  }

  /* Apply this styling to every child which is not 'DashedLine' */
  > *:not(.dashedLine) {
    ${({ $panelStyle }) => $panelStyle}
  }

  /* Some panelStyles have borders, these need to be removed before DashedLines */
  > *:not(.dashedLine):not(:first-child) {
    border-top: none !important;
  }
  > *:not(.dashedLine):not(:last-child) {
    border-bottom: none !important;
  }

  /* Fix border-radius so the widget looks like one rounded box instead of
     each segment getting its own full radius */
  > *:not(.dashedLine):not(:first-child) {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }
  > *:not(.dashedLine):not(:last-child) {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  ${hideScrollbar};
`;

type SegmentedPanelProps = {
  panelStyle: RuleSet<object>; // Aka: css`xxx`
  dashedLineVariant: VariantColor;
  noDashedLineGaps?: boolean; // Since DashedLineGaps must be applied directly near children (React limitation). Should be used depending on how the children is passed.
  noOverflow?: boolean;
} & BoxProps;

/**
 * Splits `children` into:
 * ╭---╮
 * ╰---╯
 *  - -
 * ╭---╮
 * ╰---╯
 * Adding panelStyle (e.g. bg, padding and border-radius) to each direct child and a dashedLine between them.
 * In this example `children` consist of 2 childs.
 *
 * Note: Read <DashedLinesGap/> for how child accessing is handled.
 */
export const SegmentedPanel: FCWC<SegmentedPanelProps> = ({
  panelStyle,
  dashedLineVariant,
  noDashedLineGaps,
  children,
  noOverflow,
  ...rest
}) => (
  <SegmentedPanelContainer
    className="segmentedPanel"
    $panelStyle={panelStyle}
    $noOverflow={noOverflow}
    {...rest}
  >
    {noDashedLineGaps ? (
      children
    ) : (
      <DashedLinesGap variant={dashedLineVariant}>{children}</DashedLinesGap>
    )}
  </SegmentedPanelContainer>
);
